import React from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMMKVBoolean } from "react-native-mmkv"

import { useAuth } from "src/context/AuthContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useOptionalVisemes } from "src/context/VisemeContext"
import { ChannelBrandData } from "src/services/api/api.types"
import { navigateAfterChannelLogout } from "src/utils/channelLinkUtils"
import { logSentryBreadcrumb } from "src/utils/crashReporting"
import { storage } from "src/utils/storage/mmkv"
import {
  useClearCachedConversations,
  useClearConversationStatusFlags,
  useCloseCurrentConversation,
  useCurrentConversation,
} from "./useConversation"
import { useResetRole } from "./useRole"

export const CLEAR_ALL_STORAGE_STATUS_KEY = "user.clearAllStorageStatus"

// Logout status is intentionally kept in-memory only — never persisted to any
// storage. It only needs to live for the duration of the active logout within
// this JS runtime. Persisting it (localStorage or sessionStorage) causes
// "Logging out…" to get stuck in a new tab/window: localStorage leaks it
// across tabs, and sessionStorage survives Firefox's session-restore.
let _isLoggingOut = false
const _logoutSubscribers = new Set<(value: boolean) => void>()

export function getLogoutStatus(): boolean {
  return _isLoggingOut
}

export function setLogoutStatus(isLoggingOut: boolean) {
  _isLoggingOut = isLoggingOut
  _logoutSubscribers.forEach((fn) => fn(isLoggingOut))
}

export function useLogoutStatus() {
  const [isLoggingOut, setIsLoggingOut] = React.useState(_isLoggingOut)

  React.useEffect(() => {
    _logoutSubscribers.add(setIsLoggingOut)
    return () => {
      _logoutSubscribers.delete(setIsLoggingOut)
    }
  }, [])

  return {
    isLoggingOut,
    setIsLoggingOut: setLogoutStatus,
  }
}

export function getClearAllStorageStatus(): boolean {
  return storage.getBoolean(CLEAR_ALL_STORAGE_STATUS_KEY) || false
}

export function setClearAllStorageStatus(isClearing: boolean) {
  storage.set(CLEAR_ALL_STORAGE_STATUS_KEY, isClearing)
}

export function useClearAllStorageStatus() {
  const [isClearingAllStorage, setIsClearingAllStorage] = useMMKVBoolean(
    CLEAR_ALL_STORAGE_STATUS_KEY,
  )

  return {
    isClearingAllStorage: isClearingAllStorage || false,
    setIsClearingAllStorage,
  }
}

export function useUserId() {
  const { api } = useAuth()
  return useQuery({
    queryFn: async () => {
      const response = await api.getUserId()
      if (response.kind !== "ok") {
        throw new Error("Failed to fetch user id")
      }
      return response.userId
    },
    queryKey: ["user", "id"],
  })
}

export function useMemberQuery() {
  const { api, isAuthenticated, sponsorId } = useAuth()
  const queryClient = useQueryClient()

  return useQuery({
    queryFn: async () => {
      const response = await api.getMember()
      if (response.kind !== "ok") {
        throw new Error(`Failed to fetch member: ${response.kind}`)
      }

      // Detect role drift: compare member.user_id with cached roleInfo.current_user_id
      // If they don't match, the server's role cookie may have expired and reverted
      const cachedRoleInfo = queryClient.getQueryData<{ current_user_id: string }>(["role", "info"])
      if (cachedRoleInfo && response.member.user_id !== cachedRoleInfo.current_user_id) {
        console.log(
          `[MEMBER_QUERY] Role drift detected: member.user_id="${response.member.user_id}" !== roleInfo.current_user_id="${cachedRoleInfo.current_user_id}". Invalidating role info.`,
        )
        queryClient.invalidateQueries({ queryKey: ["role", "info"] })
      }

      return response.member
    },
    queryKey: ["member"],
    enabled: !!api && isAuthenticated && !!sponsorId && sponsorId !== "unknown-sponsor",
  })
}

export function useMember() {
  const memberQuery = useMemberQuery()

  if (memberQuery.isLoading || memberQuery.isError) {
    return undefined
  }

  if (!memberQuery.data) {
    return undefined
  }

  return memberQuery.data
}

export function useUpdateMemberMsk() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  const { mutateAsync: closeConversationAsync } = useCloseCurrentConversation()
  const { data: currentConversation } = useCurrentConversation()

  return useMutation({
    mutationFn: async (mskId: string) => {
      const response = await api.updateMember({
        mskId,
      })
      if (response.kind !== "ok") {
        throw new Error("Failed to update member")
      }
      // Refetch member before returning so navigators see updated MSK (avatar) when they land on "/"
      await queryClient.refetchQueries({ queryKey: ["member"] })
      try {
        if (currentConversation != null) {
          await closeConversationAsync()
        }
      } catch (error) {
        console.error(
          "useUpdateMemberMsk: Failed to close conversation after updating member",
          error,
        )
      }
      return response.detail
    },
  })
}

export function useUpdateMemberFacts() {
  const { api } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (facts: Record<string, string>) => {
      const response = await api.updateMemberFacts(facts)
      if (response.kind !== "ok") {
        throw new Error("Failed to update member facts")
      }
      return response.detail
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["member"] })
    },
  })
}

export function useClearUserCache() {
  const queryClient = useQueryClient()

  return React.useCallback(() => {
    queryClient.removeQueries({ queryKey: ["user"] })
    queryClient.removeQueries({ queryKey: ["member"] })
  }, [queryClient])
}

export function useDeleteUserHistory() {
  const { api } = useAuth()
  const clearUserCache = useClearUserCache()
  const clearCachedConversations = useClearCachedConversations()
  const clearConversationStatusFlags = useClearConversationStatusFlags()

  return useMutation({
    mutationFn: async () => {
      const response = await api.resetDemo()
      if (response.kind !== "ok") {
        throw new Error("Failed to delete user history")
      }
      return response.result
    },
    onSuccess: () => {
      // Remove all user queries to destroy old data and force refetch
      clearUserCache()
      // Remove conversation history
      clearCachedConversations()
      clearConversationStatusFlags()
    },
  })
}

export function useResetDemo() {
  const queryClient = useQueryClient()
  const { mutateAsync: closeConversationAsync } = useCloseCurrentConversation()
  const { mutateAsync: deleteUserHistoryAsync } = useDeleteUserHistory()

  return useMutation({
    mutationFn: async () => {
      try {
        await closeConversationAsync()
      } catch (error) {
        console.error(
          "useResetDemo: Failed to close conversation before deleting user history",
          error,
        )
      }

      try {
        await deleteUserHistoryAsync(undefined)
      } catch (error) {
        console.error("useResetDemo: Failed to delete user history", error)
      }
    },
    onMutate: async () => {
      // Update a shared query's state or cache to indicate mutation start
      queryClient.setQueryData(["resetDemoStatus"], { isPending: true })
    },
    onSettled: () => {
      // Update a shared query's state or cache to indicate mutation end
      queryClient.setQueryData(["resetDemoStatus"], { isPending: false })
    },
  })
}

export function useResetDemoStatus() {
  return useQuery({
    queryKey: ["resetDemoStatus"],
    queryFn: () => ({ isPending: false }),
    initialData: { isPending: false },
  })
}

export type CleanupThenLogoutOptions = {
  /** When true, do not persist or navigate to the channel deeplink after logout. */
  skipChannelDeeplinkRedirect?: boolean
}

// Cleans up any open state for a session, then logout
// It's helpful to clean up first to reduce cleanup and error handling when next logging in
export function useCleanupThenLogout(options?: CleanupThenLogoutOptions) {
  const queryClient = useQueryClient()
  const { mutateAsync: closeCurrentConversationAsync } = useCloseCurrentConversation()
  const { logout, clearAllTemporaryData, isSessionDeactivated, sponsorId } = useAuth()
  const { setPendingChannelId } = useChannelLink()
  const visemes = useOptionalVisemes()
  const resetRoleMutation = useResetRole()

  const seedUnauthChannelInfo = (
    channelId: string,
    channelBrand: ChannelBrandData | null | undefined,
  ) => {
    if (channelBrand) {
      queryClient.setQueryData(["unauth", "channel_info", channelId], channelBrand)
    }
  }

  return useMutation({
    mutationFn: async () => {
      const channelIdForRedirect =
        !options?.skipChannelDeeplinkRedirect && sponsorId && sponsorId !== "unknown-sponsor"
          ? sponsorId
          : null

      const channelBrandForRedirect = channelIdForRedirect
        ? queryClient.getQueryData<ChannelBrandData>(["channel", "brand", channelIdForRedirect])
        : null
      const isMediaModeForRedirect = channelBrandForRedirect?.is_media_mode !== false

      logSentryBreadcrumb("user_logout_initiated")

      // Exclusive session: this device was already displaced — skip server reset/logout
      if (isSessionDeactivated) {
        clearAllTemporaryData()
        console.log("useCleanupThenLogout: session deactivated, skipped server logout")
        logSentryBreadcrumb("user_logout_complete")
        if (channelIdForRedirect) {
          setPendingChannelId(channelIdForRedirect)
          seedUnauthChannelInfo(channelIdForRedirect, channelBrandForRedirect)
          navigateAfterChannelLogout(channelIdForRedirect, isMediaModeForRedirect)
        }
        return
      }

      // Stop any ongoing audio playback before logout
      try {
        if (visemes?.stopSpeech) {
          await visemes.stopSpeech()
        }
      } catch (error) {
        console.error("useCleanupThenLogout: Failed to stop speech before logout", error)
      }

      // Close any open conversation before logout
      try {
        await closeCurrentConversationAsync()
      } catch (error) {
        console.error(
          "useCleanupThenLogout: Failed to close current conversation before logout",
          error,
        )
      }

      // Reset role before logout to properly clear permission token
      try {
        await resetRoleMutation.mutateAsync()
      } catch (error) {
        console.warn("useCleanupThenLogout: Failed to reset role before logout", error)
      }

      await logout()

      clearAllTemporaryData()
      console.log("useCleanupThenLogout: finished clearAllTemporaryData")
      logSentryBreadcrumb("user_logout_complete")

      if (channelIdForRedirect) {
        setPendingChannelId(channelIdForRedirect)
        seedUnauthChannelInfo(channelIdForRedirect, channelBrandForRedirect)
        navigateAfterChannelLogout(channelIdForRedirect, isMediaModeForRedirect)
      }
    },
    onMutate: () => {
      // Set status flag at start
      setLogoutStatus(true)
    },
    onSettled: () => {
      // Clear after the authTokens effect pass so it doesn't wipe seeded unauth data.
      setTimeout(() => setLogoutStatus(false), 0)
    },
  })
}

// Clears all storage data then logs out with status tracking
export function useClearAllStorageAndLogout() {
  const { clearAllStorageData } = useAuth()
  const cleanupThenLogout = useCleanupThenLogout({ skipChannelDeeplinkRedirect: true })

  return useMutation({
    mutationFn: async () => {
      // Then perform cleanup and logout
      await cleanupThenLogout.mutateAsync()
      // Clear all storage data last to avoid self-clearing this mutation
      clearAllStorageData()
    },
    onMutate: () => {
      // Set status flag at start
      setClearAllStorageStatus(true)
    },
    onSettled: () => {
      // Clear status flag on completion (success or error)
      setClearAllStorageStatus(false)
    },
  })
}

/**
 * Error thrown by useSponsorListQuery's queryFn. Carries the
 * GeneralApiProblem `kind` so retry / error-handling code can distinguish
 * transient failures (network/timeout/server) from terminal ones (401,
 * 403, etc.) without parsing strings.
 */
export class SponsorListFetchError extends Error {
  constructor(public readonly kind: string) {
    super(`Failed to fetch sponsor list: ${kind}`)
    this.name = "SponsorListFetchError"
  }
}

/** Terminal failure kinds — no amount of retrying will fix these. */
const TERMINAL_SPONSOR_LIST_KINDS = new Set(["unauthorized", "forbidden", "not-found", "rejected"])

export function useSponsorListQuery(opts?: { enabled?: boolean }) {
  const { api, isAuthenticated } = useAuth()

  return useQuery({
    queryFn: async () => {
      const response = await api.getSponsorList()
      if (response.kind !== "ok") {
        throw new SponsorListFetchError(response.kind)
      }
      return response.sponsors
    },
    queryKey: ["sponsors", "list"],
    enabled: (opts?.enabled ?? true) && !!api && isAuthenticated,
    // Skip retries on terminal errors. /sponsor-list is the gating
    // bootstrap query for /select-sponsor — a 401 here means the session
    // is unrecoverable (the auth interceptor in api.ts already had its
    // one shot at silent recovery before this resolved). Retrying just
    // delays the inevitable logout while the user stares at "Loading…".
    // SelectSponsor's isError effect fires triggerAutoLogout immediately
    // once we settle into the error state.
    retry: (failureCount, error) => {
      if (error instanceof SponsorListFetchError && TERMINAL_SPONSOR_LIST_KINDS.has(error.kind)) {
        return false
      }
      return failureCount < 3
    },
  })
}

export function useSponsorList() {
  const sponsorListQuery = useSponsorListQuery()

  if (sponsorListQuery.isLoading || sponsorListQuery.isError) {
    return []
  }

  if (!sponsorListQuery.data) {
    return []
  }

  return sponsorListQuery.data
}
